import React, { ReactNode, useEffect } from 'react';
import { Box } from '@mui/material';
import { Nullable, SelectOption } from 'global';
import { useFormContext, useWatch } from 'react-hook-form';
import { FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { FormSectionHeader, equipmentMileageFormatter } from 'modules/desking';
import { classOptions, equipmentConditions, equipmentYears, makeOptions } from 'modules/equipment';
import { AutocompleteValueField, CurrencyInputField, InputField, SelectField } from 'components/form';
import {
  DeskingConfigurationBaseOnAccountFormFieldsRequire,
  FinanceProgramFormValues,
  FormSectionProps,
} from 'modules/desking/types';
import { StartDealRenderProps } from '../../../types';
import { SerialNumberVinInput } from 'modules/desking/ui/components';
import { useSearchByVin } from 'modules/desking/hooks';
import { EquipmentActions } from './EquipmentActions';
import { GetDealInfoFromExternalDmsResponse } from '@trnsact/trnsact-shared-types';

type Props = FormSectionProps &
  StartDealRenderProps & {
    dynamicFieldsSlot: ReactNode;
    onChangeAllProductsConfig: (configField: string, nextValue: string | number) => void;
    formFieldsRequire: Nullable<DeskingConfigurationBaseOnAccountFormFieldsRequire>;
    externalDealInfo: GetDealInfoFromExternalDmsResponse | null;
  };

export const Equipment = ({
  sectionName,
  dynamicFieldsSlot,
  formFieldsRequire,
  externalDealInfo,
  onChangeAllProductsConfig,
}: Props) => {
  const { control, setValue } = useFormContext<FinanceProgramFormValues>();

  const { setEquipmentInfoFromDecodedValues } = useSearchByVin();

  const handleSearchByVinResult = (decodedEquipmentInfo: any) => {
    setEquipmentInfoFromDecodedValues(decodedEquipmentInfo, onChangeAllProductsConfig);
  };

  const [mileage, serialNumberVin] = useWatch({ control, name: ['equipment.mileage', 'equipment.serialNumberVin'] });

  useEffect(() => {
    if (!externalDealInfo?.equipment) {
      return;
    }

    if (externalDealInfo.equipment.quantity != null)
      setValue('equipment.quantity', externalDealInfo.equipment.quantity.toString());
    if (externalDealInfo.equipment.year != null) setValue('equipment.year', externalDealInfo.equipment.year.toString());
    if (externalDealInfo.equipment.make != null) setValue('equipment.make', externalDealInfo.equipment.make);
    if (externalDealInfo.equipment.model != null) setValue('equipment.model', externalDealInfo.equipment.model);
    if (externalDealInfo.equipment.class != null) setValue('equipment.class', externalDealInfo.equipment.class);
    if (externalDealInfo.equipment.vin != null) setValue('equipment.serialNumberVin', externalDealInfo.equipment.vin);
    if (externalDealInfo.equipment.stockNumber != null)
      setValue('equipment.stockId', externalDealInfo.equipment.stockNumber);
    if (externalDealInfo.equipment.condition != null)
      setValue('equipment.newUsed', externalDealInfo.equipment.condition);
    if (externalDealInfo.equipment.mileage != null)
      setValue('equipment.mileage', equipmentMileageFormatter(externalDealInfo.equipment.mileage, 'toView').toString());
    if (externalDealInfo.equipment.description != null)
      setValue('equipment.description', externalDealInfo.equipment.description);

    if (externalDealInfo.equipment.wholesaleCost != null)
      setValue('equipment.totalWholesaleCost', externalDealInfo.equipment.wholesaleCost.toString());
    if (externalDealInfo.equipment.retailPrice != null)
      setValue('equipment.retailPrice', externalDealInfo.equipment.retailPrice.toString());

    if (externalDealInfo.equipment.year != null)
      onChangeAllProductsConfig(FACT_NAMES_NUMERIC.EQUIPMENT_YEAR, externalDealInfo.equipment.year);
    if (externalDealInfo.equipment.make)
      onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_MAKE, String(externalDealInfo.equipment.make));
    if (externalDealInfo.equipment.class)
      onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_CLASS, String(externalDealInfo.equipment.class));
    if (externalDealInfo.equipment.vin)
      onChangeAllProductsConfig(FACT_NAMES_STRING.VIN, String(externalDealInfo.equipment.vin));
    if (externalDealInfo.equipment.condition)
      onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_CONDITION, String(externalDealInfo.equipment.condition));
    if (externalDealInfo.equipment.mileage != null)
      onChangeAllProductsConfig(FACT_NAMES_NUMERIC.ODOMETER_MILEAGE, String(externalDealInfo.equipment.mileage));
  }, [externalDealInfo]);

  return (
    <Box className="section">
      <FormSectionHeader name="Equipment" sectionName={sectionName} extraAction={<EquipmentActions />} />

      <Box className="sectionFields">
        <Box className="row">
          <InputField label="Quantity" control={control} name="equipment.quantity" />

          <AutocompleteValueField<SelectOption, false, FinanceProgramFormValues>
            control={control}
            name="equipment.year"
            options={equipmentYears}
            textFieldProps={{ label: 'Year', size: 'small' }}
            extraAction={nextValue => {
              onChangeAllProductsConfig!(FACT_NAMES_NUMERIC.EQUIPMENT_YEAR, nextValue);
            }}
          />

          <SelectField
            label="Make"
            control={control}
            name="equipment.make"
            options={makeOptions}
            extraAction={nextValue => {
              onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_MAKE, nextValue.toString());
            }}
          />

          <InputField label="Model" control={control} name="equipment.model" />

          <SelectField
            label="Class"
            control={control}
            name="equipment.class"
            options={classOptions}
            extraAction={nextValue => {
              onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_CLASS, nextValue.toString());
            }}
          />

          <SerialNumberVinInput
            handleSearchByVinResult={handleSearchByVinResult}
            inputFieldProps={{
              inputProps: {
                required: formFieldsRequire?.['equipment.serialNumberVin']?.value,
                helperText: formFieldsRequire?.['equipment.serialNumberVin']?.message,
                error: formFieldsRequire?.['equipment.serialNumberVin']?.value && !serialNumberVin,
              },
              extraAction: {
                debounceTime: 50,
                cb: (nextValue: string) => {
                  onChangeAllProductsConfig(FACT_NAMES_STRING.VIN, nextValue);
                },
              },
            }}
          />
        </Box>

        <Box className="row">
          <InputField label="Stock Id" control={control} name="equipment.stockId" />

          <SelectField
            label="Condition"
            control={control}
            name="equipment.newUsed"
            options={equipmentConditions}
            extraAction={nextValue => {
              onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_CONDITION, nextValue.toString());
            }}
          />

          <InputField
            label="Mileage"
            control={control}
            name="equipment.mileage"
            formatter={value => equipmentMileageFormatter(value, 'toUpdate')}
            inputProps={{
              required: formFieldsRequire?.['equipment.mileage']?.value,
              helperText: formFieldsRequire?.['equipment.mileage']?.message,
              error: formFieldsRequire?.['equipment.mileage']?.value && !mileage,
            }}
            extraAction={{
              cb: nextValue => {
                onChangeAllProductsConfig(FACT_NAMES_NUMERIC.ODOMETER_MILEAGE, nextValue.toString());
              },
            }}
          />

          <InputField control={control} label="Description" name="equipment.description" />

          <CurrencyInputField control={control} label="Wholesale Cost" name="equipment.totalWholesaleCost" />

          <CurrencyInputField label="Retail Price" control={control} name="equipment.retailPrice" />
        </Box>

        {dynamicFieldsSlot}
      </Box>
    </Box>
  );
};
